// ===============================================================
// file: TeamSettings.js
// ===============================================================
// React component for managing team settings and membership
// -----------------------------------------------------------
// • Client now touches **only** the /teams collection.
//   A Cloud Function called updateUserTeamsMap (deployed server-side)
//   watches /teams and updates the `teams.{teamId}` map in:
//
//       /users/{email}
//       /usersNew/{uid}
//
//   → so this file no longer writes to the user documents directly.
// • Removed helpers that wrote to /users and /usersNew.
// • UI, Tailwind styling, and permission checks are unchanged.
// -----------------------------------------------------------

import React, { useState, useEffect, useContext } from "react";
import { db } from "../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove,
  query,
  where,
  setDoc,
} from "firebase/firestore";
import { AuthContext } from "../../AuthProvider";
import { useParams } from "react-router-dom";
import {
  FaUserPlus,
  FaUserMinus,
  FaUserEdit,
  FaUserCheck,
  FaUsers,
  FaPlus,
  FaPencilAlt,
  FaCheck,
  FaTimes,
  FaInfoCircle,
  FaStar,
  FaRegStar,
  FaSync,
} from "react-icons/fa";
import { toast } from "react-toastify";

// Toggle detailed console logging here
const DEBUG = true;

const TeamSettings = ({ defaultTeamId, setDefaultTeam, isSettingDefault }) => {
  const { user } = useContext(AuthContext);
  const { teamId: urlTeamId } = useParams();
  const [teamId, setTeamId] = useState(null); // do NOT auto-select url param
  const [team, setTeam] = useState(null);
  const [teams, setTeams] = useState([]);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newTeamName, setNewTeamName] = useState("");
  const [editingTeamName, setEditingTeamName] = useState(false);
  const [editedTeamName, setEditedTeamName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [resyncLoading, setResyncLoading] = useState(false);

  // helper: conditional console logger
  const logDebug = (...args) => {
    if (DEBUG) console.log("[TeamSettings]", ...args);
  };

  // helper: validate email
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // helper: force-trigger Cloud Function by touching updatedAt
  const handleResync = async () => {
    if (!teamId) return;
    try {
      setResyncLoading(true);
      await updateDoc(doc(db, "teams", teamId), {
        updatedAt: new Date(),
      });
      toast.success("Re-Sync triggered – please wait a few seconds");
      logDebug("Manual re-sync write completed");
    } catch (err) {
      console.error(err);
      toast.error("Failed to trigger re-sync");
    } finally {
      setResyncLoading(false);
    }
  };

  // sync url param to state AFTER teams list is loaded
  useEffect(() => {
    if (urlTeamId && teams.length > 0) {
      const exists = teams.some((t) => t.id === urlTeamId);
      if (exists) setTeamId(urlTeamId);
    }
  }, [urlTeamId, teams]);

  // fetch teams where user is owner/creator/reader
  useEffect(() => {
    const fetchUserTeams = async () => {
      if (!user) return;
      try {
        setLoading(true);
        const teamsRef = collection(db, "teams");
        const ownerQ = query(
          teamsRef,
          where("owners", "array-contains", user.uid)
        );
        const creatorQ = query(
          teamsRef,
          where("creators", "array-contains", user.email)
        );
        const readerQ = query(
          teamsRef,
          where("readers", "array-contains", user.email)
        );
        const [ownSnap, creSnap, readSnap] = await Promise.all([
          getDocs(ownerQ),
          getDocs(creatorQ),
          getDocs(readerQ),
        ]);
        const map = new Map();
        ownSnap.forEach((d) =>
          map.set(d.id, { id: d.id, ...d.data(), role: "owner" })
        );
        creSnap.forEach((d) => {
          if (!map.has(d.id))
            map.set(d.id, { id: d.id, ...d.data(), role: "creator" });
        });
        readSnap.forEach((d) => {
          if (!map.has(d.id))
            map.set(d.id, { id: d.id, ...d.data(), role: "reader" });
        });
        setTeams(Array.from(map.values()));
      } catch (err) {
        console.error("Error fetching teams:", err);
        setError("Failed to load teams");
        toast.error("Failed to load teams");
      } finally {
        setLoading(false);
      }
    };
    fetchUserTeams();
  }, [user]);

  // fetch single team doc when teamId changes
  useEffect(() => {
    const fetchTeam = async () => {
      if (!teamId) {
        setTeam(null);
        return;
      }
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, "teams", teamId));
        if (snap.exists()) {
          const data = snap.data();
          setTeam(data);
          setEditedTeamName(data.name || "");
          setIsOwner(data.owners?.includes(user?.uid));
          setIsCreator(data.creators?.includes(user?.email));
        } else {
          setError("Team not found");
          setTeam(null);
          toast.error("Team not found");
        }
      } catch (err) {
        setError("Failed to load team settings");
        console.error(err);
        setTeam(null);
        toast.error("Failed to load team settings");
      } finally {
        setLoading(false);
      }
    };
    if (teamId) fetchTeam();
  }, [teamId, user?.uid, user?.email]);

  // CREATE TEAM – Cloud Function will handle user-doc mapping
  const handleCreateTeam = async () => {
    if (!newTeamName.trim()) {
      setError("Team name is required");
      toast.error("Team name is required");
      return;
    }
    try {
      setLoading(true);
      const newTeamRef = doc(collection(db, "teams"));
      await setDoc(newTeamRef, {
        name: newTeamName.trim(),
        owners: [user.uid],
        creators: [user.email],
        readers: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      toast.success("Team created successfully");
      logDebug("New team created with id:", newTeamRef.id);
      setNewTeamName("");
      setShowCreateTeam(false);
      setTeams((prev) => [
        ...prev,
        {
          id: newTeamRef.id,
          name: newTeamName.trim(),
          owners: [user.uid],
          creators: [user.email],
          readers: [],
          role: "owner",
          createdAt: new Date(),
        },
      ]);
      setTeamId(newTeamRef.id);
    } catch (err) {
      setError("Failed to create team");
      console.error(err);
      toast.error("Failed to create team: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // UPDATE TEAM NAME
  const handleUpdateTeamName = async () => {
    if (!editedTeamName.trim()) {
      setError("Team name cannot be empty");
      toast.error("Team name cannot be empty");
      return;
    }
    if (!isOwner && !isCreator) {
      setError("You do not have permission to update the team name");
      toast.error("You do not have permission to update the team name");
      return;
    }
    try {
      setLoading(true);
      await updateDoc(doc(db, "teams", teamId), {
        name: editedTeamName.trim(),
        updatedAt: new Date(),
      });
      setTeam((prev) => ({ ...prev, name: editedTeamName.trim() }));
      setTeams((prev) =>
        prev.map((t) =>
          t.id === teamId ? { ...t, name: editedTeamName.trim() } : t
        )
      );
      setEditingTeamName(false);
      toast.success("Team name updated successfully");
    } catch (err) {
      setError("Failed to update team name");
      console.error(err);
      toast.error("Failed to update team name: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ADD MEMBER – Cloud Function will update user docs
  const handleAddMember = async (role) => {
    if (!newMemberEmail || !team) return;
    if (!isValidEmail(newMemberEmail)) {
      setError("Please enter a valid email address");
      toast.error("Please enter a valid email address");
      return;
    }
    if (!isOwner) {
      setError("Only team owners can add members");
      toast.error("Only team owners can add members");
      return;
    }
    if (
      team.creators?.includes(newMemberEmail) ||
      team.readers?.includes(newMemberEmail)
    ) {
      setError("This email is already a member of the team");
      toast.error("This email is already a member of the team");
      return;
    }
    try {
      setLoading(true);
      await updateDoc(doc(db, "teams", teamId), {
        [`${role}s`]: arrayUnion(newMemberEmail),
        updatedAt: new Date(),
      });
      setTeam((prev) => ({
        ...prev,
        [`${role}s`]: [...(prev[`${role}s`] || []), newMemberEmail],
      }));
      setNewMemberEmail("");
      toast.success(`Team member added as ${role}`);
    } catch (err) {
      setError("Failed to add member");
      console.error(err);
      toast.error("Failed to add member: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // REMOVE MEMBER – Cloud Function will clean user docs
  const handleRemoveMember = async (email, role) => {
    if (!team) return;
    if (email === user.email && !isOwner && role === "creator") {
      setError("You cannot remove yourself from the team");
      toast.error("You cannot remove yourself from the team");
      return;
    }
    if (!isOwner) {
      setError("Only team owners can remove members");
      toast.error("Only team owners can remove members");
      return;
    }
    if (
      !window.confirm(`Are you sure you want to remove ${email} from the team?`)
    )
      return;
    try {
      setLoading(true);
      await updateDoc(doc(db, "teams", teamId), {
        [`${role}s`]: arrayRemove(email),
        updatedAt: new Date(),
      });
      setTeam((prev) => ({
        ...prev,
        [`${role}s`]: prev[`${role}s`].filter((e) => e !== email),
      }));
      toast.success("Team member removed");
    } catch (err) {
      setError("Failed to remove member");
      console.error(err);
      toast.error("Failed to remove member: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // CHANGE ROLE – Cloud Function will swap mapping
  const handleChangeRole = async (email, fromRole, toRole) => {
    if (!team) return;
    if (!isOwner) {
      setError("Only team owners can change member roles");
      toast.error("Only team owners can change member roles");
      return;
    }
    try {
      setLoading(true);
      await updateDoc(doc(db, "teams", teamId), {
        [`${fromRole}s`]: arrayRemove(email),
        [`${toRole}s`]: arrayUnion(email),
        updatedAt: new Date(),
      });
      setTeam((prev) => ({
        ...prev,
        [`${fromRole}s`]: prev[`${fromRole}s`].filter((e) => e !== email),
        [`${toRole}s`]: [...(prev[`${toRole}s`] || []), email],
      }));
      toast.success(`Member role changed to ${toRole}`);
    } catch (err) {
      setError("Failed to change member role");
      console.error(err);
      toast.error("Failed to change member role: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // set default team (unchanged)
  const handleSetDefaultTeam = (tId, tName) => {
    if (setDefaultTeam) setDefaultTeam(tId, tName);
  };

  // RENDER – list view or details view
  if (loading && teamId)
    return <div className="py-8 text-center">Loading team settings...</div>;

  // Render team selector when no team is selected
  if (!teamId || !team) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <h2 className="text-2xl font-bold mb-6">Team Settings</h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Your Teams</h3>
            <button
              onClick={() => setShowCreateTeam(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
            >
              <FaPlus className="mr-2" /> Create New Team
            </button>
          </div>

          {showCreateTeam && (
            <div className="mb-6 bg-purple-50 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Create New Team</h4>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="Enter team name"
                  className="flex-1 p-2 border rounded"
                />
                <button
                  onClick={handleCreateTeam}
                  className="bg-purple-600 text-white px-4 py-2 rounded"
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Create"}
                </button>
                <button
                  onClick={() => setShowCreateTeam(false)}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded"
                  disabled={loading}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {teams.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-lg">
              <FaUsers className="mx-auto text-4xl text-gray-400 mb-3" />
              <p className="text-gray-600 mb-4">
                You don't have any teams yet.
              </p>
              <button
                onClick={() => setShowCreateTeam(true)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Create Your First Team
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {teams.map((team) => (
                <div
                  key={team.id}
                  className="border rounded-lg p-4 hover:bg-gray-50"
                >
                  <div className="flex justify-between items-center">
                    <div
                      className="flex-1 cursor-pointer"
                      onClick={() => setTeamId(team.id)}
                    >
                      <h4 className="font-medium text-lg">
                        {team.name}
                        {defaultTeamId === team.id && (
                          <span className="ml-2 text-yellow-500 text-sm">
                            (Default)
                          </span>
                        )}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {team.role === "owner"
                          ? "Team Owner"
                          : team.role === "creator"
                          ? "Creator Access"
                          : "Reader Access"}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleSetDefaultTeam(team.id, team.name)}
                        disabled={isSettingDefault || defaultTeamId === team.id}
                        className={`text-lg ${
                          defaultTeamId === team.id
                            ? "text-yellow-500"
                            : "text-gray-400 hover:text-yellow-500"
                        } transition-colors`}
                        title={
                          defaultTeamId === team.id
                            ? "Default Team"
                            : "Set as Default Team"
                        }
                      >
                        {defaultTeamId === team.id ? <FaStar /> : <FaRegStar />}
                      </button>
                      <div className="text-sm text-gray-500">
                        {team.createdAt?.toDate
                          ? new Date(
                              team.createdAt.toDate()
                            ).toLocaleDateString()
                          : team.createdAt instanceof Date
                          ? team.createdAt.toLocaleDateString()
                          : "Date unknown"}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Normal team settings view when a team is selected
  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <h2 className="text-2xl font-bold">Team Settings</h2>
          {defaultTeamId === teamId ? (
            <span className="ml-3 text-yellow-500 flex items-center">
              <FaStar className="mr-1" /> Default Team
            </span>
          ) : (
            <button
              onClick={() => handleSetDefaultTeam(teamId, team.name)}
              disabled={isSettingDefault}
              className="ml-3 text-gray-500 hover:text-yellow-500 flex items-center transition-colors"
            >
              <FaRegStar className="mr-1" /> Set as Default
            </button>
          )}
          {isOwner && (
            <button
              onClick={handleResync}
              disabled={resyncLoading}
              className="ml-3 text-gray-500 hover:text-blue-500 flex items-center transition-colors"
              title="Force Cloud Re-Sync"
            >
              <FaSync
                className={`mr-1 ${resyncLoading ? "animate-spin" : ""}`}
              />{" "}
              {resyncLoading ? "Re-Syncing..." : "Re-Sync"}
            </button>
          )}
        </div>
        <button
          onClick={() => setTeamId(null)}
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          ← Back to Teams
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Team Info */}
      <div className="mb-8 bg-white p-4 rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Team Information</h3>

          {(isOwner || isCreator) && !editingTeamName && (
            <button
              onClick={() => setEditingTeamName(true)}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
            >
              <FaPencilAlt className="mr-1" /> Edit Team Name
            </button>
          )}
        </div>

        {editingTeamName ? (
          <div className="mb-4 flex items-center">
            <label className="block text-sm font-medium text-gray-700 mr-2">
              Team Name:
            </label>
            <input
              type="text"
              value={editedTeamName}
              onChange={(e) => setEditedTeamName(e.target.value)}
              className="flex-1 p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <button
              onClick={handleUpdateTeamName}
              className="ml-2 text-green-600 hover:text-green-800"
              title="Save"
            >
              <FaCheck />
            </button>
            <button
              onClick={() => {
                setEditingTeamName(false);
                setEditedTeamName(team.name || "");
              }}
              className="ml-2 text-red-600 hover:text-red-800"
              title="Cancel"
            >
              <FaTimes />
            </button>
          </div>
        ) : (
          <p className="text-gray-600 mb-2">
            <span className="font-medium">Team Name:</span> {team.name}
          </p>
        )}

        <p className="text-gray-600">
          <span className="font-medium">Created:</span>{" "}
          {new Date(
            team.createdAt?.toDate?.() || team.createdAt
          ).toLocaleDateString()}
        </p>
        {team.updatedAt && (
          <p className="text-gray-600">
            <span className="font-medium">Last Updated:</span>{" "}
            {new Date(
              team.updatedAt?.toDate?.() || team.updatedAt
            ).toLocaleDateString()}
          </p>
        )}
      </div>

      {/* Teams permission explanation */}
      <div className="mb-8 bg-blue-50 p-4 rounded-lg border border-blue-200">
        <div className="flex items-start">
          <FaInfoCircle className="text-blue-500 mt-1 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium mb-1">Team Roles</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>
                <span className="font-medium">Owners:</span> Can add/remove
                members and manage all team settings
              </li>
              <li>
                <span className="font-medium">Creators:</span> Can create and
                edit content within the team
              </li>
              <li>
                <span className="font-medium">Readers:</span> Can view content
                but cannot make changes
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add New Member */}
      {isOwner && (
        <div className="mb-8 bg-white p-4 rounded-lg shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Add New Member</h3>
          <div className="flex gap-4">
            <input
              type="email"
              value={newMemberEmail}
              onChange={(e) => setNewMemberEmail(e.target.value)}
              placeholder="Enter email address"
              className="flex-1 p-2 border rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <button
              onClick={() => handleAddMember("creator")}
              className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-600"
              disabled={loading || !newMemberEmail}
            >
              <FaUserPlus /> Add as Creator
            </button>
            <button
              onClick={() => handleAddMember("reader")}
              className="bg-green-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-green-600"
              disabled={loading || !newMemberEmail}
            >
              <FaUserPlus /> Add as Reader
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            * Creators can edit content, readers can only view
          </p>
        </div>
      )}

      {/* Creators List */}
      <div className="mb-8 bg-white p-4 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Creators</h3>
        <div className="space-y-2">
          {team.creators?.length > 0 ? (
            team.creators.map((email) => (
              <div
                key={email}
                className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100"
              >
                <div className="flex items-center gap-2">
                  <FaUserEdit className="text-blue-500" />
                  <span>{email}</span>
                  {email === user.email && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                      You
                    </span>
                  )}
                </div>
                {isOwner && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() =>
                        handleChangeRole(email, "creator", "reader")
                      }
                      className="text-gray-600 hover:text-gray-800 text-sm bg-gray-200 px-2 py-1 rounded"
                      title="Change to Reader"
                    >
                      Make Reader
                    </button>
                    <button
                      onClick={() => handleRemoveMember(email, "creator")}
                      className="text-red-500 hover:text-red-700"
                      title="Remove from team"
                    >
                      <FaUserMinus />
                    </button>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic text-center py-4">
              No creators added to this team
            </p>
          )}
        </div>
      </div>

      {/* Readers List */}
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <h3 className="text-lg font-semibold mb-4">Readers</h3>
        <div className="space-y-2">
          {team.readers?.length > 0 ? (
            team.readers.map((email) => (
              <div
                key={email}
                className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-100"
              >
                <div className="flex items-center gap-2">
                  <FaUserCheck className="text-green-500" />
                  <span>{email}</span>
                  {email === user.email && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                      You
                    </span>
                  )}
                </div>
                {isOwner && (
                  <div className="flex space-x-2">
                    <button
                      onClick={() =>
                        handleChangeRole(email, "reader", "creator")
                      }
                      className="text-gray-600 hover:text-gray-800 text-sm bg-gray-200 px-2 py-1 rounded"
                      title="Change to Creator"
                    >
                      Make Creator
                    </button>
                    <button
                      onClick={() => handleRemoveMember(email, "reader")}
                      className="text-red-500 hover:text-red-700"
                      title="Remove from team"
                    >
                      <FaUserMinus />
                    </button>
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic text-center py-4">
              No readers added to this team
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamSettings;
